import React, { useState, useEffect, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import { API_BASE, fetchCsrfToken } from './adminShared';
import './ForestAnalysis.css';

const STATUS_COLORS = {
  healthy_monitoring: '#1a6b3c',
  suspected: '#c47f17',
  confirmed_active: '#c1362c',
  dead_standing: '#5a5a5a',
  dead_fallen: '#2f2f2f',
  hazard: '#7a1f13',
  treated: '#3968b0',
};

const STATUS_ORDER = [
  'confirmed_active',
  'dead_standing',
  'dead_fallen',
  'hazard',
  'suspected',
  'treated',
  'healthy_monitoring',
];

const STATUS_LABELS = {
  healthy_monitoring: 'Healthy',
  suspected: 'Suspected',
  confirmed_active: 'Active',
  dead_standing: 'Dead — standing',
  dead_fallen: 'Dead — fallen',
  hazard: 'Hazard',
  treated: 'Treated',
};

const SPECIES_OPTIONS = [
  ['ponderosa', 'Ponderosa pine'],
  ['lodgepole', 'Lodgepole pine'],
  ['limber', 'Limber pine'],
  ['bristlecone', 'Bristlecone pine'],
  ['whitebark', 'Whitebark pine'],
  ['scotch', 'Scotch pine'],
  ['douglas_fir', 'Douglas-fir'],
  ['engelmann_spruce', 'Engelmann spruce'],
  ['blue_spruce', 'Blue spruce'],
  ['subalpine_fir', 'Subalpine fir'],
  ['aspen', 'Aspen'],
  ['juniper', 'Juniper'],
  ['other', 'Other / unknown'],
];

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function timeUntilExpiry(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return '< 1h';
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function RunsList({ runs, loading, onOpen, onRefresh }) {
  return (
    <div className="fa-runs-wrap">
      <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
        <div>
          <strong>Recent runs</strong>
          <span className="text-muted small ms-2">
            Cache-only · runs expire after 7 days
          </span>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={onRefresh} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
        </Button>
      </div>
      {runs.length === 0 ? (
        <div className="text-center py-5 text-muted">
          No runs yet. Open the Map sub-tab, frame an area, and click <strong>Analyze this view</strong>.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table admin-table mb-0">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Area</th>
                <th>Threshold</th>
                <th>Scenes</th>
                <th>Candidates</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="admin-row">
                  <td>{formatDate(r.created_at)}</td>
                  <td>{r.created_by}</td>
                  <td>{r.area_km2} km²</td>
                  <td className="font-monospace">{r.threshold}</td>
                  <td>{r.scene_count}</td>
                  <td>
                    <Badge bg={r.candidate_count > 0 ? 'warning' : 'secondary'} text={r.candidate_count > 0 ? 'dark' : undefined}>
                      {r.candidate_count}
                    </Badge>
                  </td>
                  <td className="text-muted small">{timeUntilExpiry(r.expires_at)}</td>
                  <td>
                    <Button size="sm" variant="primary" onClick={() => onOpen(r.id)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CandidateReview({ run, onBack, onRunUpdated }) {
  const [idx, setIdx] = useState(0);
  const [species, setSpecies] = useState('lodgepole');
  const [status, setStatus] = useState('confirmed_active');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const candidates = run.candidates || [];
  const total = candidates.length;
  const current = candidates[idx];
  const currentId = current ? current.id : null;

  useEffect(() => {
    setSpecies('lodgepole');
    setStatus('confirmed_active');
    setNotes('');
    setErr(null);
  }, [currentId]);

  const advance = (updatedRun) => {
    onRunUpdated(updatedRun);
    // After removal the candidate at idx is now a different one — same idx is fine
    // unless we walked past the end.
    if (updatedRun.candidates.length === 0) return;
    if (idx >= updatedRun.candidates.length) setIdx(updatedRun.candidates.length - 1);
  };

  const confirm = async () => {
    if (!current) return;
    setBusy(true);
    setErr(null);
    try {
      const csrf = await fetchCsrfToken();
      const res = await fetch(
        `${API_BASE}/admin/analysis-runs/${run.id}/candidates/${current.id}/confirm/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
          body: JSON.stringify({ status, species, notes }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Confirm failed');
      advance(data.run);
    } catch (e) {
      setErr(e.message || 'Confirm failed');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!current) return;
    setBusy(true);
    setErr(null);
    try {
      const csrf = await fetchCsrfToken();
      const res = await fetch(
        `${API_BASE}/admin/analysis-runs/${run.id}/candidates/${current.id}/reject/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reject failed');
      advance(data.run);
    } catch (e) {
      setErr(e.message || 'Reject failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fa-review">
      <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
        <div>
          <Button size="sm" variant="link" onClick={onBack} className="p-0 me-3">← Back to runs</Button>
          <strong>Run {run.id.slice(0, 8)}</strong>
          <span className="text-muted small ms-3">
            {run.area_km2} km² · threshold {run.params.threshold} · {run.scene_count} scenes
          </span>
        </div>
        <div className="text-muted small">
          {total > 0 ? `${idx + 1} / ${total}` : 'Queue empty'}
        </div>
      </div>

      {total === 0 ? (
        <div className="text-center py-5 text-muted">
          All candidates in this run have been reviewed.
        </div>
      ) : (
        <div className="fa-review-body">
          <div className="fa-pane fa-pane-evidence">
            <div className="fa-pane-title">Algorithmic evidence</div>
            <div className="fa-metric-grid">
              <div><span>Severity</span><strong>{current.severity_score}</strong></div>
              <div><span>NDMI ref</span><strong>{current.ndmi_reference}</strong></div>
              <div><span>NDMI now</span><strong>{current.ndmi_current}</strong></div>
              <div><span>Δ NDMI</span><strong>{(current.ndmi_current - current.ndmi_reference).toFixed(3)}</strong></div>
            </div>
            <div className="fa-coords">
              {current.centroid_lat.toFixed(5)}, {current.centroid_lng.toFixed(5)}
            </div>
            <div className="fa-pane-note">
              Sentinel-2 change-detection is stubbed in Phase 3; polygons are synthetic. Real
              NDMI diffs land in Phase 4.
            </div>
          </div>

          <div className="fa-pane fa-pane-decision">
            <div className="fa-pane-title">Decision</div>

            <Row className="g-2 mb-2 align-items-center">
              <Col xs="auto"><label className="fa-label">Species</label></Col>
              <Col>
                <Form.Select
                  size="sm"
                  value={species}
                  onChange={(e) => setSpecies(e.target.value)}
                  disabled={busy}
                >
                  {SPECIES_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Form.Select>
              </Col>
            </Row>

            <label className="fa-label">Confirm as status</label>
            <div className="fm-status-chips mb-2">
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`fm-status-chip ${status === s ? 'active' : ''}`}
                  style={{
                    backgroundColor: status === s ? STATUS_COLORS[s] : 'transparent',
                    borderColor: STATUS_COLORS[s],
                    color: status === s ? '#fff' : STATUS_COLORS[s],
                  }}
                  onClick={() => setStatus(s)}
                  disabled={busy}
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>

            <label className="fa-label">Notes (optional)</label>
            <Form.Control
              as="textarea"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={busy}
              placeholder="Anything worth recording alongside this observation…"
            />

            {err && <div className="fa-error mt-2">{err}</div>}

            <div className="fa-decision-actions mt-3">
              <Button variant="outline-danger" onClick={reject} disabled={busy}>
                Reject
              </Button>
              <Button variant="success" onClick={confirm} disabled={busy}>
                {busy ? 'Saving…' : 'Confirm →'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {total > 0 && (
        <div className="fa-review-footer">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx <= 0 || busy}
          >
            ← Previous
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
            disabled={idx >= total - 1 || busy}
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
}

function ForestAnalysis({ openRunId, onRunOpened }) {
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedErr, setSelectedErr] = useState(null);

  const fetchRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/analysis-runs/`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRuns(data.runs || []);
      }
    } catch { /* silent */ } finally {
      setRunsLoading(false);
    }
  }, []);

  const openRun = useCallback(async (runId) => {
    setSelectedLoading(true);
    setSelectedErr(null);
    try {
      const res = await fetch(`${API_BASE}/admin/analysis-runs/${runId}/`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Run not found');
      setSelectedRun(data);
    } catch (e) {
      setSelectedErr(e.message);
      setSelectedRun(null);
    } finally {
      setSelectedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // If parent hands us a run id (map "Review" link → runs sub-tab), open it.
  useEffect(() => {
    if (openRunId) {
      openRun(openRunId);
      if (onRunOpened) onRunOpened();
    }
  }, [openRunId, openRun, onRunOpened]);

  if (selectedLoading) {
    return <div className="text-center py-5 text-muted">Loading run…</div>;
  }
  if (selectedErr) {
    return (
      <div className="p-4">
        <div className="fa-error">{selectedErr}</div>
        <Button size="sm" className="mt-2" onClick={() => { setSelectedErr(null); setSelectedRun(null); }}>
          Back to runs
        </Button>
      </div>
    );
  }
  if (selectedRun) {
    return (
      <CandidateReview
        run={selectedRun}
        onBack={() => { setSelectedRun(null); fetchRuns(); }}
        onRunUpdated={setSelectedRun}
      />
    );
  }
  return (
    <RunsList
      runs={runs}
      loading={runsLoading}
      onOpen={openRun}
      onRefresh={fetchRuns}
    />
  );
}

export default ForestAnalysis;
